import {
  createPublicClient,
  createWalletClient,
  custom,
  http,
  type Address,
  type EIP1193Provider,
  type Hex,
  type PublicClient,
  type WalletClient,
} from "viem";
import {
  arbitrum,
  base,
  bsc,
  mainnet,
  optimism,
  polygon,
  type Chain,
} from "viem/chains";

import { findChain } from "../data/tokens.js";

/**
 * Per-chain viem `Chain` registry for the playground. Each entry maps a
 * swapgate-supported chain id to the canonical viem definition (RPC URLs
 * are NOT used at signing time — the injected wallet provider carries
 * its own endpoint; viem's `Chain` shape is consumed for EIP-1193
 * chain-switch params and tx serialization defaults).
 *
 * `data/tokens.ts` is the single source of truth for "which chains are
 * supported"; this map is the wallet-side complement.
 */
const VIEM_CHAINS: Readonly<Record<number, Chain>> = {
  [polygon.id]: polygon,
  [base.id]: base,
  [arbitrum.id]: arbitrum,
  [optimism.id]: optimism,
  [mainnet.id]: mainnet,
  [bsc.id]: bsc,
};

export function viemChainFor(chainId: number): Chain | undefined {
  return VIEM_CHAINS[chainId];
}

/**
 * Curated list of free public RPCs by chain, used when no
 * `VITE_RPC_<chainId>` env var is provided. We DO NOT use viem's
 * built-in defaults verbatim because some of them (e.g. `eth.merkle.io`
 * for mainnet) flake on heavier methods like `eth_getBalance` — they
 * answer `eth_blockNumber` instantly but stall on state reads, which
 * makes the BalancePill fail silently while everything else "looks ok".
 *
 * Order matters: the first URL is the primary; the rest exist as
 * documentation for the next fallback when we add multi-endpoint
 * rotation (currently viem only uses the first).
 *
 * Public endpoints are rate-limited. For production-grade traffic,
 * override with `VITE_RPC_<chainId>` pointing at Alchemy/Quicknode/Ankr.
 */
const PUBLIC_RPCS: Record<number, readonly string[]> = {
  [polygon.id]: [
    "https://polygon-rpc.com",
    "https://1rpc.io/matic",
    "https://polygon.llamarpc.com",
  ],
  [mainnet.id]: [
    "https://eth.llamarpc.com",
    "https://1rpc.io/eth",
    "https://cloudflare-eth.com",
  ],
  [base.id]: [
    "https://base.llamarpc.com",
    "https://mainnet.base.org",
    "https://1rpc.io/base",
  ],
  [arbitrum.id]: [
    "https://arbitrum.llamarpc.com",
    "https://1rpc.io/arb",
    "https://arb1.arbitrum.io/rpc",
  ],
  [optimism.id]: [
    "https://optimism.llamarpc.com",
    "https://mainnet.optimism.io",
    "https://1rpc.io/op",
  ],
  [bsc.id]: [
    "https://binance.llamarpc.com",
    "https://bsc-dataseed.binance.org",
    "https://1rpc.io/bnb",
  ],
};

/**
 * Resolve the HTTP RPC URL the playground uses for read-only polling
 * (`publicClientFor`). Lookup order:
 *
 *   1. Build-time env var `VITE_RPC_<CHAINID>` (e.g. `VITE_RPC_137`).
 *      Set in `apps/playground/.env.production` or via the CI workflow.
 *      THIS GOES INTO THE PUBLIC BUNDLE — use a key with read-only scope.
 *   2. Curated public RPC from `PUBLIC_RPCS` (see above).
 *   3. Viem's chain default — last resort, only if we don't know the chain.
 */
function rpcUrlFor(
  chainId: number,
  chain: Chain | undefined,
): string | undefined {
  const envKey = `VITE_RPC_${chainId}` as const;
  const envUrl = (import.meta.env as Record<string, string | undefined>)[
    envKey
  ];
  if (envUrl !== undefined && envUrl !== "") return envUrl;
  const curated = PUBLIC_RPCS[chainId]?.[0];
  if (curated !== undefined) return curated;
  return chain?.rpcUrls.default.http[0];
}

/**
 * Narrowed handle to the injected provider (`window.ethereum` from
 * MetaMask, Rabby, Brave, etc.). EIP-1193 is the only contract we
 * commit to — wallet-specific extensions (`isMetaMask`, etc.) are
 * irrelevant for V1.1.
 */
declare global {
  interface Window {
    ethereum?: EIP1193Provider;
  }
}

export function getInjectedProvider(): EIP1193Provider | undefined {
  if (typeof window === "undefined") return undefined;
  return window.ethereum;
}

export interface WalletState {
  readonly address: Address;
  readonly chainId: number;
}

/**
 * Request wallet connection (eth_requestAccounts) + current chainId.
 * Returns `undefined` when the user dismisses the connect prompt rather
 * than throwing — the playground UI distinguishes "not connected" from
 * "error" via two separate states.
 */
export async function connectWallet(
  provider: EIP1193Provider,
): Promise<WalletState | undefined> {
  try {
    const accounts = (await provider.request({
      method: "eth_requestAccounts",
    })) as readonly Address[];
    if (accounts.length === 0) return undefined;
    const chainIdHex = (await provider.request({
      method: "eth_chainId",
    })) as Hex;
    return { address: accounts[0]!, chainId: Number.parseInt(chainIdHex, 16) };
  } catch (err) {
    // MetaMask uses code 4001 for "user rejected request".
    if (
      typeof err === "object" &&
      err !== null &&
      "code" in err &&
      (err as { code: number }).code === 4001
    ) {
      return undefined;
    }
    throw err;
  }
}

/**
 * Switch the wallet to `chainId`, prompting `wallet_switchEthereumChain`
 * and falling back to `wallet_addEthereumChain` when the chain isn't
 * known to the wallet (error code 4902).
 *
 * Throws on any non-recoverable error; resolves once the chain change
 * has been confirmed via `eth_chainId` so subsequent tx broadcasts can
 * assume the wallet is on the right network.
 */
export async function switchChain(
  provider: EIP1193Provider,
  chainId: number,
): Promise<void> {
  const target = viemChainFor(chainId);
  if (target === undefined) {
    throw new Error(`Chain ${chainId} is not in the playground registry`);
  }

  const hexId = `0x${chainId.toString(16)}` as Hex;
  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: hexId }],
    });
    return;
  } catch (err) {
    const code =
      typeof err === "object" && err !== null && "code" in err
        ? (err as { code: number }).code
        : 0;
    if (code !== 4902) {
      throw err;
    }
  }

  // 4902 → chain not yet added. Prompt the user with the canonical viem
  // chain metadata (the first public RPC + the canonical explorer).
  const meta = findChain(chainId);
  const rpcUrl = target.rpcUrls.default.http[0] ?? "https://cloudflare-eth.com";
  const blockExplorer = target.blockExplorers?.default;
  await provider.request({
    method: "wallet_addEthereumChain",
    params: [
      {
        chainId: hexId,
        chainName: target.name,
        nativeCurrency: target.nativeCurrency,
        rpcUrls: [rpcUrl],
        ...(blockExplorer !== undefined
          ? { blockExplorerUrls: [blockExplorer.url] }
          : {}),
      },
    ],
  });
  // Make sure the wallet honored the switch after add. Some wallets
  // (Rabby) add but don't switch automatically.
  const currentHex = (await provider.request({ method: "eth_chainId" })) as Hex;
  if (Number.parseInt(currentHex, 16) !== chainId) {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: hexId }],
    });
  }
  // Touch findChain so the unused-import lint stays quiet — its lookup
  // is also useful in the catch-shaped error message below.
  void meta;
}

/**
 * Build a viem `WalletClient` bound to the injected provider and the
 * given chain. Used once per step in `StepsExecutor` to call
 * `sendTransaction` with the freshest chain context (the steps array
 * may span multiple chains in the BEFORE flow, although V1.0 only
 * issues same-chain quotes).
 */
export function walletClientFor(
  provider: EIP1193Provider,
  chainId: number,
  account: Address,
): WalletClient {
  const chain = viemChainFor(chainId);
  return createWalletClient({
    account,
    chain,
    transport: custom(provider),
  });
}

/**
 * Build a viem `PublicClient` for read-only RPC polling.
 *
 * Deliberately **NOT** bound to the wallet's `window.ethereum` provider.
 * MetaMask/Rabby route reads through their internal RPC proxy (often
 * Infura's free tier), which lags ~10–30s on `eth_getTransactionReceipt`
 * for txs they just broadcast — long enough to time out
 * `waitForTransactionReceipt` and leave the user staring at a
 * confirmed-on-explorer tx that the UI still calls "pending".
 *
 * Instead we use the chain's default HTTP RPC (the public endpoint
 * baked into viem's chain definitions, e.g. polygon-rpc.com). It's
 * not authenticated and rate-limited, but for a playground polling
 * a single tx hash it's plenty.
 *
 * Hardening:
 *   - `pollingInterval: 2_000` — faster than viem's 4s default; tx
 *     usually lands within 2-3 blocks on Polygon (~5s).
 *   - `wait timeout: 120_000` at the call site — twice the viem default
 *     to absorb worst-case RPC propagation lag.
 *
 * `provider` is kept in the signature for API parity with
 * `walletClientFor`; future versions may add a wallet-provider
 * fallback if the public RPC is down.
 */
export function publicClientFor(
  _provider: EIP1193Provider,
  chainId: number,
): PublicClient {
  const chain = viemChainFor(chainId);
  const rpcUrl = rpcUrlFor(chainId, chain);
  return createPublicClient({
    chain,
    transport: rpcUrl !== undefined ? http(rpcUrl) : http(),
    pollingInterval: 2_000,
  });
}
